import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { Modal } from 'flowbite';
import { AppComponent } from 'src/app/app.component';
import { getBookingForm, getStops, getCancelledBookingForm, getServiceInfoRoutes, formatDate } from 'src/app/forms/bookingForms';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import { IBookingUpdate } from 'src/app/models/booking/bookingUpdate';
import { ExpeditionStatus, ExpeditionType, NotificationTypes, TicketingType, UserType, InputType } from 'src/app/models/enums';
import { ErrorsCode } from 'src/app/models/enums/errorsCode';
import { BookingsService, NotificationService, RouteService, TownsService, UtilsService } from 'src/app/services';
import { FavouritesService } from 'src/app/services/favourites/favourites.service';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import { IServiceAvailability } from 'src/app/models';

@Component({
    selector: 'app-booking-details',
    templateUrl: './booking-details.component.html',
    styleUrl: './booking-details.component.scss',
    standalone: false
})
export class BookingDetailsComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() booking: IBookingDetails = {} as IBookingDetails;
  @Output() openInFullMap = new EventEmitter<IBookingDetails>();
  @Output() editBooking = new EventEmitter<IBookingDetails>();
  @Output() updateBookings = new EventEmitter();
  @Output() deleteBooking = new EventEmitter();
  @Output() markAsAFault = new EventEmitter();
  @Output() markSeatAsFault = new EventEmitter<any>();
  @Output() closeDetails = new EventEmitter();
  @Output() showTicketingStatusModal = new EventEmitter();

  public cancellationReason = this.literalService.get('bookings.data.reason', true);

  public expanded = false;
  public changeKmsShowed = false;
  public changeSeatsShowed = false;
  public changeAvailabilityShowed = false;
  public changeOriginDestinationShowed = false;
  public updatingOriginDestination = false;
  public createFavouriteShowed = false;
  public setFaultShowed = false;
  public exceedingKmLimitShowed = false;

  public edit = false;
  public newBooking = false;
  public editable = false;

  public bookingForm: any;

  public bookingCopy: IBookingDetails;
  public isHoveringOriginDestinationDetail: boolean;
  public changeKms: Modal;
  public changeSeats: Modal;
  public changeAvailability: Modal;
  public changeOriginDestination: Modal;
  public createFavouriteModal: Modal;
  public setFaultModal: Modal;
  public exceedingKmLimitModal: Modal;

  public creatingFavourite = false;

  public routesElement: any;
  public availabilitiesElement: any;
  public loadingAvailabilities = false;
  public availablityElement: any;
  public originElement: any;
  public destinationElement: any;

  public images = images;
  public TooltipPosition = TooltipPosition;

  public faultModalShow = false;

  public maxKmsPerMonth: number;
  public kmsTravelled: number;

  constructor(
    public literalService: LiteralService,
    public router: Router,
    public appComponent: AppComponent,
    private routeService: RouteService,
    private serviceInfoService: ServiceInfoService,
    private notificationService: NotificationService,
    private bookingsService: BookingsService,
    public utilsService: UtilsService,
    private favouritesService: FavouritesService,
    private townsService: TownsService
  ) { }

  ngOnInit() {}

  ngAfterViewInit(): void {
    this.loadForm();
    if (this.booking) {
      this.editable = this.booking.tripStatus != ExpeditionStatus.CLOSED && this.booking.tripType != ExpeditionType.HybridDoor2Door && this.booking.tripType != ExpeditionType.DynamicDoor2Door && this.booking.tripType != ExpeditionType.DynamicWithFixedPoints;
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.loadForm();
    if (changes['booking']) {
      this.bookingForm = await getBookingForm(changes['booking'].currentValue, this.literalService);
      this.editable = this.booking && this.booking.tripStatus != ExpeditionStatus.CLOSED && this.booking.tripType != ExpeditionType.HybridDoor2Door && this.booking.tripType != ExpeditionType.DynamicDoor2Door && this.booking.tripType != ExpeditionType.DynamicWithFixedPoints;
    }
  }

  async loadForm() {
    if (!this.booking) return;
    if (this.booking.cancelled) {
      this.bookingForm = await getCancelledBookingForm(this.booking);
    } else {
      this.bookingForm = await getBookingForm(this.booking, this.literalService);
    }
  };

  showCreateFavouriteModal() {
    this.createFavouriteModal = new Modal(document.getElementById('createFavouriteFromDetail'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.createFavouriteShowed = false),
      onShow: () => (this.createFavouriteShowed = true),
    });
    this.createFavouriteModal.toggle();
  }

  showSetFaultModal() {
    this.setFaultModal = new Modal(document.getElementById('setFault'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.setFaultShowed = false),
      onShow: () => (this.setFaultShowed = true),
    });
    this.setFaultModal.toggle();
  }

  closeModals() {
    if (this.changeKmsShowed) this.changeKms.toggle();
    if (this.changeSeatsShowed) this.changeSeats.toggle();
    if (this.changeAvailabilityShowed) this.changeAvailability.toggle();
    if (this.changeOriginDestinationShowed) this.changeOriginDestination.toggle();
    if (this.createFavouriteShowed) this.createFavouriteModal.toggle();
    if (this.setFaultShowed) this.setFaultModal.toggle();
  };

  async openModal(modal: any) {
    this.bookingCopy = JSON.parse(JSON.stringify(this.booking));

    if (['origin', 'destination'].includes(modal)) {
      const originStops = await getStops(this.townsService, this.booking);
      const destinationStops = await getStops(this.townsService, this.booking, this.bookingCopy.origin.serviceStopId);
      ['originStop', 'destinationStop'].forEach((key) => {
          const element = this.bookingForm.find((x: { title: string }) => x.title === `bookings.data.${key}`);
          if (element?.edit) element.edit = { ...element.edit, values: key === 'originStop' ? originStops : destinationStops };
          key === 'originStop' ? this.originElement = element : this.destinationElement = element;
      });

      this.originElement.value = this.bookingCopy.origin.serviceStopId;
      this.destinationElement.value = this.bookingCopy.destination.serviceStopId;

      this.changeOriginDestination = new Modal(document.getElementById('changeOriginDestination'), {
        closable: false,
        onHide: () => { this.originElement = null; this.destinationElement = null; this.changeOriginDestinationShowed = false; },
        onShow: () => (this.changeOriginDestinationShowed = true),
      });
      this.changeOriginDestination.toggle();
    } else if (modal === 'kms') {
      this.booking.kms = this.booking.kms ? Number(this.booking.kms.toFixed(2)) : 0;
      this.changeKms = new Modal(document.getElementById('changeKms'), {
        closable: false,
        onHide: () => (this.changeKmsShowed = false),
        onShow: () => (this.changeKmsShowed = true),
      });
      this.changeKms.toggle();
    } else if (modal === 'seats') {
      this.changeSeats = new Modal(document.getElementById('changeSeats-detail'), {
        closable: false,
        onHide: () => (this.changeSeatsShowed = false),
        onShow: () => (this.changeSeatsShowed = true),
      });
      this.changeSeats.toggle();
    } else if (modal === 'availability') {
      this.routesElement = {
        img: images.services.route,
        title: 'services.routes.routeTitle',
        color: true,
        value: this.booking.routeId,
        edit: {
          inputType: InputType.SELECT_SEARCH,
          placeholder: this.literalService.get('bookings.actions.reassignBookings.inputPlaceholder', true),
          values: await getServiceInfoRoutes(this.serviceInfoService, this.booking),
        }
      };
      this.availabilitiesElement = null;
      this.changeAvailability = new Modal(document.getElementById('changeAvailability'), {
        closable: false,
        onHide: () => (this.changeAvailabilityShowed = false),
        onShow: () => (this.changeAvailabilityShowed = true),
      });
      this.changeAvailability.toggle();
      this.getRouteAvailabilities({ id: this.booking.routeId });
    } else if (modal === 'status' && this.booking.ticketingType && this.booking.ticketingType !== TicketingType.None && !(this.booking.ticketingType === TicketingType.Online && this.booking.price === null)) {
      this.showTicketingStatusModal.emit();
    }
  }

  async getRouteAvailabilities(data: any) {
    this.loadingAvailabilities = true;
    await this.routeService.getAvailabilities(data.id, 0, 100).then((res) => {
      let availabilities: any = []
      res.content.map((availability: IServiceAvailability) => {
        availabilities.push(
          {
            id: availability.id,
            value: availability.id,
            label: `${formatDate(availability.startDateTime, this.literalService)} (${availability.driver!.name} - ${availability.vehicle!.name})`
          }
        );
      });
      this.availabilitiesElement = {
        img: images.booking.availability,
        title: 'bookings.actions.reassignBookings.inputTitle',
        color: true,
        value: this.booking.availability?.id,
        edit: {
          inputType: InputType.SELECT_SEARCH,
          placeholder: this.literalService.get('bookings.actions.reassignBookings.inputPlaceholder', true),
          values: availabilities
        },
      };
    });
    this.loadingAvailabilities = false;
  }

  submitEdit(booking: IBookingDetails) {
    if (this.changeOriginDestinationShowed) {
      this.submitBookingEdit(booking);
      this.changeOriginDestination.hide();
    } else {
      this.submitBookingKmsEdit(booking);
    }
  }

  async submitBookingEdit(data: any) {
    let submitBooking = data.booking as IBookingDetails
    this.updatingOriginDestination = true;
    const bookingUpdate: IBookingUpdate = {} as IBookingUpdate;
    bookingUpdate.loggedUserId = submitBooking.userId;
    bookingUpdate.availabilityId = submitBooking.availability.id;
    bookingUpdate.tripId = submitBooking.tripId;
    bookingUpdate.bookingId = submitBooking.id;
    bookingUpdate.targetUserId = submitBooking.userId;
    bookingUpdate.originStop = submitBooking.origin;
    bookingUpdate.destinationStop = submitBooking.destination;
    bookingUpdate.seats = submitBooking.seats;
    bookingUpdate.prmSeats = submitBooking.prmSeats;
    bookingUpdate.bookingSeats = submitBooking.bookingSeats;
    bookingUpdate.sendNotification = data.sendNotification;
    bookingUpdate.recomputePrice = data.recomputePrice;
    try {
      await this.bookingsService.updateBooking(bookingUpdate);
      this.updateBookings.emit();
      this.utilsService.showNotification(images.notifications.check, 'bookings.actions.editBooking', NotificationTypes.SUCCESS);
    } catch (error: any) {
      this.notificationService.image = images.notification.error;
      this.notificationService.title = this.literalService.get('bookings.actions.editBooking.errorTitle');
      this.notificationService.message = '0';
      this.notificationService.translate = true;
      if (error.message.includes(ErrorsCode.MAX_CAPACITY)) {
        if (submitBooking.seats === this.booking.seats && submitBooking.prmSeats !== this.booking.prmSeats) {
          this.notificationService.message = this.literalService.get('bookings.actions.editBooking.errorMaxPRMCapacity');
        } else {
          this.notificationService.message = this.literalService.get('bookings.actions.editBooking.errorMaxCapacity');
        }
      }
      this.notificationService.show(NotificationTypes.DANGER);
    }
    this.updatingOriginDestination = false;
    if (this.changeOriginDestinationShowed) this.changeOriginDestination.toggle();
  };

  async submitBookingKmsEdit(booking: IBookingDetails) {
    try {
      this.booking = await this.bookingsService.updateKms(this.booking.id!, booking.kms);
      this.bookingForm = await getBookingForm(this.booking, this.literalService);
      this.utilsService.showNotification(images.notifications.check, 'bookings.actions.editBooking', NotificationTypes.SUCCESS);
    } catch (error) {
      this.notificationService.image = images.notification.error;
      this.notificationService.title = 'errorOccurred';
      this.notificationService.message = '0';
      this.notificationService.translate = true;
      this.notificationService.show(NotificationTypes.DANGER);
    }
  };

  handleExceedingKmsLimit(booking: IBookingDetails) {
    this.kmsTravelled = booking.kms;
    this.maxKmsPerMonth = booking.service.routes[0].maxKmsPerMonth || 0;
    this.exceedingKmLimitModal = new Modal(document.getElementById('exceedingKmLimitEdit'), {
      placement: 'center',
      closable: false,
      onHide: () => (this.exceedingKmLimitShowed = false),
      onShow: () => (this.exceedingKmLimitShowed = true),
    });
    this.exceedingKmLimitModal.show();
  };

  closeExceedingKm() {
    this.exceedingKmLimitModal.hide();
    this.kmsTravelled = 0;
    this.maxKmsPerMonth = 0;
  };

  setIsHoveringOriginDestinationDetail(isHovering: boolean) {
    this.isHoveringOriginDestinationDetail = isHovering;
  };

  close() {
    this.expanded = false;
    this.edit = false;
    this.newBooking = false;
    this.closeDetails.emit();
  };

  goToUser(userId: number, userType: UserType): void {
    this.appComponent.setRouteName('users');
    this.router.navigate([`/users${userType === UserType.Driver ? `/${userType.toLowerCase()}` : ''}/${userId}`]);
  }

  goToTrip(tripId: number): void {
    this.appComponent.setRouteName('trips');
    this.router.navigate(['/trips/' + tripId]);
  }

  async createFavourite(favouriteName: string) {
    this.creatingFavourite = true;
    await this.favouritesService.createFavouriteFromBooking(favouriteName, this.booking).then(() => {
      this.utilsService.showNotification(images.notifications.check, 'bookings.actions.createFavourite.success', NotificationTypes.SUCCESS);
    }, (error: any) => {
      this.creatingFavourite = false;
    });
    this.createFavouriteModal.toggle();
    this.creatingFavourite = false;
  };

  getBookingStatus(): 'disabled' | 'error' | 'warning' | 'validated' | 'ok' {
    const { ticketingType, price, amountPaid, paidDateTime, validatedDateTime } = this.booking;

    if (ticketingType === TicketingType.None) return 'disabled';
    if (validatedDateTime) return paidDateTime ? 'ok' : 'validated';
    if (price || [TicketingType.Hybrid, TicketingType.Onboard, TicketingType.Integrated].includes(ticketingType)) {
        if (!paidDateTime) return 'error';
        const underpaid = amountPaid != null && amountPaid < price;
        return underpaid ? 'error' : 'warning';
    }
    return 'disabled';
  }

  getTicketProperties() {
    const status = this.getBookingStatus();
    const imageMapping = {
      disabled: this.images.booking.ticket.ticket,
      error: this.images.booking.ticket.ticketError,
      warning: this.images.booking.ticket.ticketWarning,
      validated: this.images.booking.ticket.ticketOk,
      ok: this.images.booking.ticket.ticketOk,
    };
    const ticketName = `ticket${status.charAt(0).toUpperCase() + status.slice(1)}`;
    return {
      src: imageMapping[status] || imageMapping.disabled,
      alt: ticketName,
      tooltip: this.literalService.get(`bookings.tooltips.${ticketName}`, true)
    };
  }

  getTicketPriceColor(): string {
    const status = this.getBookingStatus();
    const colorMapping: Record<string, string> = {
      disabled: 'neutrals-grey-03-text',
      error: 'secondary-red-text',
      warning: 'secondary-yellow-text',
      validated: 'secondary-green-text',
      ok: 'secondary-green-text',
    };
    return colorMapping[status] || colorMapping['disabled'];
  }

  checkIfBookingNeedsEditConfirmation() {
    if (this.bookingCopy) {
      return (
        this.bookingCopy.ticketingType === TicketingType.Online ||
        this.bookingCopy.ticketingType === TicketingType.Hybrid
      ) && this.bookingCopy.price > 0
    }
    return false;
  }

}
